/**
 * 取引先マスタ(vendors・旧: 支払い先マスタ)と登録依頼(vendor_requests)。
 * 2026-10-23: 共通マスタの取引先として区分・宛名・住所・インボイス番号・支払条件を追加(docs/specs/2026-10-全体設計.md 3章)。
 * 申請フォームは選択のみ(自由入力禁止)。新規は経理へ登録依頼するフロー。
 * counterparty からの自動生成で表記ゆれ重複が出るため、マージ機能を持つ(補足指示C)。
 */
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { nextMasterCode } from '@core/lib/numbering';
import { writeAuditLog } from '@core/lib/auditLog';
import type { PaymentTerms, Vendor, VendorRequest } from '@core/lib/types';
import type { PartnerInput } from '@core/partners/partnerCsv';
import { normalizeInvoiceRegNo, validateTerms } from '@core/partners/terms';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export async function fetchVendors(includeInactive = false): Promise<Vendor[]> {
  const snap = await getDocs(query(collection(getDb(), 'vendors'), orderBy('name')));
  return snap.docs.map((d) => withId<Vendor>(d)).filter((v) => includeInactive || v.active);
}

/**
 * 取引先の追加項目(2026-10-23 共通マスタ)。未入力は null。区分が空なら「仕入先・支払先」。
 * 更新(forCreate=false)では、渡されなかった項目(undefined)は書き換えない(古い呼び出し元で消えないように)
 */
function partnerFields(input: Partial<PartnerInput>, forCreate: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const has = (k: keyof PartnerInput) => forCreate || input[k] !== undefined;
  const text = (v: string | undefined) => (v ?? '').trim() || null;
  const terms = (t: PaymentTerms | null | undefined, label: string) => {
    if (!t) return null;
    const invalid = validateTerms(t);
    if (invalid) throw new Error(`${label}: ${invalid}`);
    return t;
  };
  if (has('kinds')) out.kinds = input.kinds && input.kinds.length > 0 ? input.kinds : ['supplier'];
  if (has('legalName')) out.legal_name = text(input.legalName);
  if (has('invoiceAddressee')) out.invoice_addressee = text(input.invoiceAddressee);
  if (has('postalCode')) out.postal_code = text(input.postalCode);
  if (has('address')) out.address = text(input.address);
  if (has('invoiceRegNo')) {
    const raw = (input.invoiceRegNo ?? '').trim();
    const reg = raw ? normalizeInvoiceRegNo(raw) : null;
    if (raw && !reg) throw new Error('インボイス登録番号は T+13桁 で入力してください');
    out.invoice_reg_no = reg;
  }
  if (has('payTerms')) out.pay_terms = terms(input.payTerms, '支払条件');
  if (has('collectTerms')) out.collect_terms = terms(input.collectTerms, '入金条件');
  return out;
}

export async function createVendor(
  input: { name: string; bankInfo: string; note: string } & Partial<PartnerInput>,
  userId: string,
): Promise<string> {
  const name = input.name.trim();
  if (!name) throw new Error('取引先の名称を入力してください');
  const extra = partnerFields(input, true);
  const code = await nextMasterCode('V');
  const ref = doc(collection(getDb(), 'vendors'));
  await setDoc(ref, {
    code,
    name,
    bank_info: input.bankInfo.trim() || null,
    note: input.note.trim() || null,
    ...extra,
    active: true,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  await writeAuditLog({
    targetType: 'master',
    targetId: ref.id,
    action: 'vendor-create',
    by: userId,
    after: { code, name },
  });
  return ref.id;
}

export async function updateVendor(
  id: string,
  patch: { name: string; bankInfo: string; note: string; active: boolean } & Partial<PartnerInput>,
): Promise<void> {
  if (!patch.name.trim()) throw new Error('名称を入力してください');
  const extra = partnerFields(patch, false);
  await updateDoc(doc(getDb(), 'vendors', id), {
    name: patch.name.trim(),
    bank_info: patch.bankInfo.trim() || null,
    note: patch.note.trim() || null,
    ...extra,
    active: patch.active,
    updated_at: serverTimestamp(),
  });
}

/**
 * 一括取り込み(2026-10-23): 確認済みの「新規」の行だけを1件ずつ登録する。
 * 途中で失敗したら、そこで止めて登録できた件数と失敗した行を返す(登録済みの分は残る・二重登録はしない)
 */
export async function bulkCreateVendors(
  inputs: Array<{ line: number; input: PartnerInput }>,
  userId: string,
): Promise<{ created: number; failed: { line: number; message: string } | null }> {
  let created = 0;
  for (const { line, input } of inputs) {
    try {
      await createVendor(input, userId);
      created++;
    } catch (e) {
      return { created, failed: { line, message: e instanceof Error ? e.message : '登録に失敗しました' } };
    }
  }
  return { created, failed: null };
}

/**
 * マージ: 統合元(source)に紐づく取引・固定費をすべて統合先(target)へ付け替え、
 * 統合元は無効化する(削除はしない。管理番号の履歴を残すため)。
 */
export async function mergeVendors(params: {
  sourceId: string;
  targetId: string;
  userId: string;
}): Promise<{ moved: number }> {
  const { sourceId, targetId, userId } = params;
  if (sourceId === targetId) throw new Error('統合元と統合先が同じです');
  const db = getDb();

  const txSnap = await getDocs(
    query(collection(db, 'transactions'), where('vendor_id', '==', sourceId)),
  );
  let moved = 0;
  for (let i = 0; i < txSnap.docs.length; i += 400) {
    const batch = writeBatch(db);
    for (const d of txSnap.docs.slice(i, i + 400)) {
      batch.update(d.ref, { vendor_id: targetId });
      moved++;
    }
    await batch.commit();
  }

  await updateDoc(doc(db, 'vendors', sourceId), {
    active: false,
    note: `統合済み(→ ${targetId})`,
    updated_at: serverTimestamp(),
  });

  await writeAuditLog({
    targetType: 'master',
    targetId: sourceId,
    action: 'vendor-merge',
    by: userId,
    after: { target: targetId, moved },
  });
  return { moved };
}

// --- 登録依頼 ---------------------------------------------------------------

export async function fetchVendorRequests(openOnly = true): Promise<VendorRequest[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'vendor_requests'), orderBy('requested_at', 'desc')),
  );
  return snap.docs
    .map((d) => withId<VendorRequest>(d))
    .filter((r) => !openOnly || r.status === 'open');
}

/** 申請者からの登録依頼(申請フォームの「＋新しい支払い先の登録を依頼」) */
export async function createVendorRequest(
  input: { name: string; note: string },
  userId: string,
): Promise<void> {
  if (!input.name.trim()) throw new Error('支払い先の名称を入力してください');
  await setDoc(doc(collection(getDb(), 'vendor_requests')), {
    name: input.name.trim(),
    note: input.note.trim() || null,
    status: 'open',
    requested_by: userId,
    requested_at: serverTimestamp(),
    resolved_by: null,
    resolved_at: null,
    vendor_id: null,
  });
}

/** 依頼を承認して vendors に登録する(経理) */
export async function resolveVendorRequest(request: VendorRequest, userId: string): Promise<void> {
  const vendorId = await createVendor({ name: request.name, bankInfo: '', note: request.note ?? '' }, userId);
  await updateDoc(doc(getDb(), 'vendor_requests', request.id), {
    status: 'done',
    resolved_by: userId,
    resolved_at: serverTimestamp(),
    vendor_id: vendorId,
  });
}

export async function dismissVendorRequest(id: string, userId: string): Promise<void> {
  await updateDoc(doc(getDb(), 'vendor_requests', id), {
    status: 'dismissed',
    resolved_by: userId,
    resolved_at: serverTimestamp(),
  });
}
